import type {
  tConnectionState,
  tEmbeddedSignupErrorReport,
} from 'src/hooks/whatsapp/whatsappConnect.types';
import { toIntlLocale } from 'src/functions/date.functions';
import {
  WIZARD_ACTIVATION_ESCALATION_MINUTES,
  WIZARD_ACTIVATION_FAILURE_REASON_KEYS,
  WIZARD_ERROR_CATALOG,
  WIZARD_EXCHANGE_ERROR_CODES_WITH_VERDICT,
  WIZARD_HISTORY_NOT_SHARED_ERROR_CODE,
  WIZARD_META_STEP_BY_SIGNUP_STEP,
} from './WhatsAppConnectionWizard.constants';
import type {
  tWizardActivationFailure,
  tWizardClosedStep,
  tWizardErrorCatalogEntry,
  tWizardExchangeError,
  tWizardFailureFooter,
  tWizardHistorySync,
} from './WhatsAppConnectionWizard.types';

/**
 * Close-history key of a close Meta reported no step for. Meta sends no
 * `CANCEL` step on its intro or phone-number screens, so these closes still
 * count against each other. Lowercase so it never equals one of Meta's steps.
 */
export const UNKNOWN_CLOSE_STEP_KEY = 'unknown_step';

/** Where the person is in the connection, as the wizard tells it apart. */
export type tConnectionMoment =
  | { kind: 'choose_path' }
  | { kind: 'meta_window_open' }
  | { kind: 'meta_error_window_open'; errorReport: tEmbeddedSignupErrorReport }
  | {
      kind: 'meta_error_window_closed';
      errorReport: tEmbeddedSignupErrorReport;
    }
  | {
      kind: 'closed_before_finishing';
      /** Meta's raw step, or null when Meta reported none. */
      step: string | null;
      /** True when the previous consecutive close was at the same step. */
      isRepeat: boolean;
    }
  | { kind: 'activating' }
  | { kind: 'verifying' }
  | { kind: 'activation_failed' }
  | { kind: 'connected' }
  | { kind: 'exchange_error' }
  | { kind: 'escalated' };

/**
 * Turns the Meta step of a close into its close-history key.
 *
 * @param {string | null} signupStep - Meta's raw `current_step`, if any.
 * @returns {string} The step itself, or `UNKNOWN_CLOSE_STEP_KEY`.
 */
export function toCloseStepKey(signupStep: string | null): string {
  return signupStep ?? UNKNOWN_CLOSE_STEP_KEY;
}

/**
 * Resolves the connection moment from the shared connection state and the
 * keys of the consecutive closes of Meta's window, oldest first.
 *
 * @param {object} options - What the moment is resolved from.
 * @param {tConnectionState} options.state - The shared connection state.
 * @param {string[]} options.closedStepKeys - Consecutive close keys, oldest first.
 * @returns {tConnectionMoment} The moment the wizard is in.
 */
export function resolveConnectionMoment(options: {
  state: tConnectionState;
  closedStepKeys: readonly string[];
}): tConnectionMoment {
  const { state, closedStepKeys } = options;

  switch (state.phase) {
    case 'connect':
    case 'skipped':
      return { kind: 'choose_path' };

    case 'connecting':
      // Once Meta handed over a code, a report from its window no longer
      // decides anything: the exchange does.
      if (state.isExchangingCode) return { kind: 'activating' };
      if (state.signupErrorReport) {
        return {
          kind: 'meta_error_window_open',
          errorReport: state.signupErrorReport,
        };
      }
      return { kind: 'meta_window_open' };

    case 'failed_closed': {
      if (state.signupErrorReport) {
        return {
          kind: 'meta_error_window_closed',
          errorReport: state.signupErrorReport,
        };
      }
      const closeCount = closedStepKeys.length;
      return {
        kind: 'closed_before_finishing',
        step: state.signupStep,
        isRepeat:
          closeCount >= 2 &&
          closedStepKeys[closeCount - 1] === closedStepKeys[closeCount - 2],
      };
    }

    case 'failed_error':
      return { kind: 'exchange_error' };

    case 'success':
      return { kind: 'connected' };

    case 'verifying':
    case 'activation_failed':
    case 'escalated':
      return { kind: state.phase };
  }
}

/**
 * Finds the catalog entry of the error code Meta reported. Only the code is
 * matched, never Meta's text, so an entry without a known code is never found.
 *
 * @param {string | number | null | undefined} metaErrorCode - The reported code.
 * @returns {tWizardErrorCatalogEntry | undefined} The entry, or undefined when
 * the code is missing or out of the catalog.
 */
export function findWizardErrorCatalogEntry(
  metaErrorCode: string | number | null | undefined,
): tWizardErrorCatalogEntry | undefined {
  if (metaErrorCode === null || metaErrorCode === undefined) return undefined;

  const normalizedCode = String(metaErrorCode).trim();
  if (normalizedCode === '') return undefined;

  return WIZARD_ERROR_CATALOG.find(
    (entry) => entry.metaErrorCode === normalizedCode,
  );
}

/**
 * Names the Meta screen a close ended on. A close on a step the wizard does
 * not know reads like one with no step, so it never counts as a repeat: the
 * repeat names its step.
 *
 * @param {object} close - The close, as `closed_before_finishing` carries it.
 * @param {string | null} close.step - Meta's raw `current_step`, if any.
 * @param {boolean} close.isRepeat - Whether the previous close had the same key.
 * @returns {tWizardClosedStep} The named screen and whether it repeats.
 */
export function resolveClosedStep(close: {
  step: string | null;
  isRepeat: boolean;
}): tWizardClosedStep {
  const metaStep = close.step
    ? WIZARD_META_STEP_BY_SIGNUP_STEP.get(close.step)
    : undefined;
  return { metaStep, isRepeat: close.isRepeat && metaStep !== undefined };
}

/**
 * Picks the footer actions of a failure once Meta's window is closed: the
 * catalog's primary for a known error, the advisor for a repeated close, and
 * the retry otherwise.
 *
 * @param {tConnectionMoment} moment - The moment the wizard is in.
 * @returns {tWizardFailureFooter | null} The footer, or null for a moment
 * without this footer.
 */
export function resolveFailureFooter(
  moment: tConnectionMoment,
): tWizardFailureFooter | null {
  const plainRetryLabelKey = 'wizard.actions.retry';

  if (moment.kind === 'closed_before_finishing') {
    return {
      primary: resolveClosedStep(moment).isRepeat ? 'advisor' : 'retry',
      retryLabelKey: plainRetryLabelKey,
    };
  }
  if (moment.kind !== 'meta_error_window_closed') return null;

  const entry = findWizardErrorCatalogEntry(moment.errorReport.code);
  if (!entry) return { primary: 'retry', retryLabelKey: plainRetryLabelKey };
  const retryLabelKey =
    entry.retry.presentation === 'ghost'
      ? entry.retry.labelKey
      : plainRetryLabelKey;
  if (entry.footerPrimary === 'openInMeta') {
    return {
      primary: 'openInMeta',
      retryLabelKey,
      metaLinkLabelKey: `wizard.errorCatalog.${entry.id}.metaLinkLabel`,
      metaUrl: entry.metaUrl,
    };
  }
  return { primary: entry.footerPrimary, retryLabelKey };
}

/**
 * Resolves the activation failure screen: copy by reason, and a footer that
 * leads with the advisor when retrying no longer fixes it, for a reason the
 * wizard cannot explain or from the second consecutive failure on.
 *
 * @param {object} failure - The failure on screen.
 * @param {string | null} failure.reason - WBS `activation_reason`, verbatim.
 * @param {number} failure.failureCount - Consecutive activation failures, this one included.
 * @returns {tWizardActivationFailure} The copy group, repeat and footer.
 */
export function resolveActivationFailure(failure: {
  reason: string | null;
  failureCount: number;
}): tWizardActivationFailure {
  const reasonKey =
    (failure.reason &&
      WIZARD_ACTIVATION_FAILURE_REASON_KEYS.get(failure.reason)) ||
    'unknown';
  const isRepeat = failure.failureCount >= 2;
  return {
    reasonKey,
    isRepeat,
    footer: {
      primary: isRepeat || reasonKey === 'unknown' ? 'advisor' : 'retry',
      retryLabelKey: 'wizard.actions.retry',
    },
  };
}

/**
 * Whether a failed exchange carries no verdict: no error code, or one the
 * wizard has no cause for, such as a 500 or the wait limit.
 *
 * @param {string | null} errorCode - The failed exchange's error code, if any.
 * @returns {boolean} False only for a code with its own cause.
 */
export function isExchangeErrorWithoutVerdict(
  errorCode: string | null,
): boolean {
  return !errorCode || !WIZARD_EXCHANGE_ERROR_CODES_WITH_VERDICT.has(errorCode);
}

/**
 * Resolves the exchange error screen with no verdict: the footer leads with
 * the retry the first time and with the advisor from the second consecutive
 * error on, since retrying already failed twice.
 *
 * @param {object} failure - The failure on screen.
 * @param {string | null} failure.errorCode - The failed exchange's error code.
 * @param {number} failure.errorCount - Consecutive exchange errors with no
 * verdict, this one included.
 * @returns {tWizardExchangeError | null} The repeat and footer, or null for a
 * code with its own cause, whose screen carries its own actions.
 */
export function resolveExchangeError(failure: {
  errorCode: string | null;
  errorCount: number;
}): tWizardExchangeError | null {
  if (!isExchangeErrorWithoutVerdict(failure.errorCode)) return null;
  const isRepeat = failure.errorCount >= 2;
  return {
    isRepeat,
    footer: {
      primary: isRepeat ? 'advisor' : 'retry',
      retryLabelKey: 'wizard.actions.retry',
    },
  };
}

/**
 * Whether the wizard offers to connect without the guide, in the current
 * modal: on the step to choose a path, and on a failure whose footer leads
 * with the retry. Where the advisor or Meta leads, the current modal fails the
 * same way, and while Meta's window is open leaving would strand it.
 *
 * @param {tConnectionMoment} moment - The moment the wizard is in.
 * @param {tWizardFailureFooter | null} failureFooter - The footer of the
 * failure on screen, or null for a moment without one.
 * @returns {boolean} True when the link to the current modal shows.
 */
export function canConnectWithoutGuide(
  moment: tConnectionMoment,
  failureFooter: tWizardFailureFooter | null,
): boolean {
  if (moment.kind === 'choose_path') return true;
  return failureFooter?.primary === 'retry';
}

/**
 * Whether Meta has held the number past the time WBS escalates it to support.
 *
 * @param {object} options - What the check reads.
 * @param {string | undefined} options.activationDecidedAt - When Meta's decision
 * was recorded, as the integration row sends it.
 * @param {number} options.now - The current time, in milliseconds.
 * @returns {number | null} Milliseconds left until the escalation, zero or
 * less once it passed, or null when the time is missing or not a date.
 */
export function millisecondsUntilActivationEscalation(options: {
  activationDecidedAt: string | undefined;
  now: number;
}): number | null {
  if (!options.activationDecidedAt) return null;
  const decidedAt = Date.parse(options.activationDecidedAt);
  if (Number.isNaN(decidedAt)) return null;
  return (
    decidedAt + WIZARD_ACTIVATION_ESCALATION_MINUTES * 60_000 - options.now
  );
}

/**
 * Whether the browser window fills the screen. Meta's window then cannot sit
 * beside the wizard: browsers open it as a tab or at full size. A screen that
 * reports no size, as some privacy settings and headless browsers do, says
 * nothing about the window, so it never counts as full screen.
 *
 * @returns {boolean} True when the window is at least as large as the screen.
 */
export function isWindowFullScreen(): boolean {
  return (
    window.screen.width > 0 &&
    window.outerWidth >= window.screen.width &&
    window.outerHeight >= window.screen.height
  );
}

/**
 * Resolves the history sync the connected screen tells, from ProfilingProgress
 * for a real connection. A history that was not shared wins over a running
 * sync: nothing of the history is coming.
 *
 * @param {object} sync - What ProfilingProgress publishes.
 * @param {boolean} sync.isActive - Whether a sync or profiling run is going.
 * @param {number} [sync.errorCode] - The sync status `error_code`, if any.
 * @returns {tWizardHistorySync} The history sync to tell.
 */
export function resolveHistorySync(sync: {
  isActive: boolean;
  errorCode?: number;
}): tWizardHistorySync {
  if (sync.errorCode === WIZARD_HISTORY_NOT_SHARED_ERROR_CODE) {
    return 'notShared';
  }
  return sync.isActive ? 'syncing' : 'idle';
}

/**
 * Formats the day a Meta screen was captured as a long date in the app
 * language, e.g. "7 de octubre de 2026". The day is read and formatted in UTC:
 * a date-only value is midnight UTC, which the local zone of a negative offset
 * would show as the day before.
 *
 * @param {object} options - The capture day and the app language.
 * @param {string} options.capturedOn - The day as `YYYY-MM-DD`.
 * @param {string} options.language - The app language (`es`, `en`, `br`).
 * @returns {string} The localized date.
 */
export function formatCaptureDate(options: {
  capturedOn: string;
  language: string;
}): string {
  return new Intl.DateTimeFormat(toIntlLocale(options.language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${options.capturedOn}T00:00:00Z`));
}
